import apiSlice from "./api/api";
import {
  PAYROLL_SALARY,
  PAYROLL_PAYSLIPS,
  PAYROLL_PROCESS,
  PAYROLL_SALARY_COMPONENTS
} from "./ENDPOINTS";

const payrollApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Salary Components
    getSalaryComponents: builder.query({
      query: ({ type }) => {
        const params = new URLSearchParams();
        if (type) params.append('type', type);

        return {
          url: `/payroll/components?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["SalaryComponents"],
    }),

    createSalaryComponent: builder.mutation({
      query: (data) => ({
        url: `/payroll/components`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["SalaryComponents"],
    }),

    updateSalaryComponent: builder.mutation({
      query: ({ id, data }) => ({
        url: `/payroll/components/${id}`,
        method: "PUT",
        body: data
      }),
      invalidatesTags: ["SalaryComponents"],
    }),

    deleteSalaryComponent: builder.mutation({
      query: (id) => ({
        url: `/payroll/components/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SalaryComponents"],
    }),

    // Employee Salary
    assignEmployeeSalary: builder.mutation({
      query: (data) => ({
        url: `/payroll/employee-salary`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["EmployeeSalary", "Payroll"],
    }),

    getEmployeeSalaryStructure: builder.query({
      query: (employeeId) => ({
        url: `/payroll/employee-salary/${employeeId}`,
        method: "GET",
      }),
      providesTags: ["EmployeeSalary"],
    }),

    updateEmployeeSalary: builder.mutation({
      query: ({ employeeId, data }) => ({
        url: `/payroll/employee-salary/${employeeId}`,
        method: "PUT",
        body: data
      }),
      invalidatesTags: ["EmployeeSalary", "Payroll"],
    }),

    // Payroll Generation
    generatePayroll: builder.mutation({
      query: (data) => ({
        url: `/payroll/generate`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Payroll", "Payslips"],
    }),

    generateEmployeePayroll: builder.mutation({
      query: ({ employeeId, month, year }) => ({
        url: `/payroll/generate/${employeeId}?month=${month}&year=${year}`,
        method: "POST",
      }),
      invalidatesTags: ["Payroll", "Payslips"],
    }),

    // Get Payrolls
    getPayrolls: builder.query({
      query: ({ month, year, department_id, status, page, limit }) => {
        const params = new URLSearchParams();
        if (month) params.append('month', month);
        if (year) params.append('year', year);
        if (department_id) params.append('department_id', department_id);
        if (status) params.append('status', status);
        if (page) params.append('page', page);
        if (limit) params.append('limit', limit);

        return {
          url: `/payroll?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Payroll"],
    }),

    getPayrollById: builder.query({
      query: (id) => ({
        url: `/payroll/${id}`,
        method: "GET",
      }),
      providesTags: ["Payroll"],
    }),

    // Payroll Actions
    submitPayroll: builder.mutation({
      query: (id) => ({
        url: `/payroll/${id}/submit`,
        method: "POST",
      }),
      invalidatesTags: ["Payroll"],
    }),

    approvePayroll: builder.mutation({
      query: ({ id, data }) => ({
        url: `/payroll/${id}/approve`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Payroll"],
    }),

    rejectPayroll: builder.mutation({
      query: ({ id, data }) => ({
        url: `/payroll/${id}/reject`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Payroll"],
    }),

    markPayrollAsPaid: builder.mutation({
      query: ({ id, data }) => ({
        url: `/payroll/${id}/mark-paid`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Payroll", "Payslips"],
    }),

    // Payslips
    getEmployeePayslips: builder.query({
      query: ({ employeeId, year, month }) => {
        const params = new URLSearchParams();
        if (year) params.append('year', year);
        if (month) params.append('month', month);

        return {
          url: `/payroll/payslips/${employeeId}?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Payslips"],
    }),

    // Get payslips for logged-in employee
    getMyPayslips: builder.query({
      query: ({ year, month, page = 1, limit = 10 }) => {
        const params = new URLSearchParams();
        if (year) params.append('year', year);
        if (month) params.append('month', month);
        if (page) params.append('page', page);
        if (limit) params.append('limit', limit);

        return {
          url: `/payroll/my-payslips?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Payslips"],
    }),

    downloadPayslip: builder.query({
      query: ({ payrollId, format = 'pdf' }) => ({
        url: `/payroll/${payrollId}/payslip?format=${format}`,
        method: "GET",
      }),
    }),

    // Payroll Reports
    getPayrollSummary: builder.query({
      query: ({ month, year, department_id }) => {
        const params = new URLSearchParams();
        if (month) params.append('month', month);
        if (year) params.append('year', year);
        if (department_id) params.append('department_id', department_id);

        return {
          url: `/payroll/reports/summary?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["PayrollReports"],
    }),

    exportPayroll: builder.query({
      query: ({ month, year, format = 'excel' }) => ({
        url: `/payroll/export?month=${month}&year=${year}&format=${format}`,
        method: "GET",
      }),
    }),
  })
});

export const {
  useLazyGetSalaryComponentsQuery,
  useCreateSalaryComponentMutation,
  useUpdateSalaryComponentMutation,
  useDeleteSalaryComponentMutation,
  useAssignEmployeeSalaryMutation,
  useLazyGetEmployeeSalaryStructureQuery,
  useUpdateEmployeeSalaryMutation,
  useGeneratePayrollMutation,
  useGenerateEmployeePayrollMutation,
  useLazyGetPayrollsQuery,
  useLazyGetPayrollByIdQuery,
  useSubmitPayrollMutation,
  useApprovePayrollMutation,
  useRejectPayrollMutation,
  useMarkPayrollAsPaidMutation,
  useLazyGetEmployeePayslipsQuery,
  useLazyGetMyPayslipsQuery,
  useLazyDownloadPayslipQuery,
  useLazyGetPayrollSummaryQuery,
  useLazyExportPayrollQuery,
} = payrollApiSlice;
